import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomeTextField from "./CustomeTextField"; // Adjust the path as needed
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  parentContainer: {
    marginBottom: "15px",
  },
  childContainer: {
    marginRight: 5,
    height: 15,
    width: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
  },
  branchWrapper: {
    padding: "4px 8px",
  },
  branchLeafWrapper: {
    padding: "0",
    marginLeft: 20,
  },
  branchRow: {
    display: "flex",
    alignItems: "center",
  },
  childrenContainer: {
    marginTop: 6,
    maxHeight: 150,
    overflowY: "scroll",
  },
  container: {
    marginTop: 0,
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "4px 8px",
    maxWidth: 300,
  },
}));

const CustomTextSelect = ({
  data,
  label,
  onSelect,
  initiallyExpanded = false,
  selectedBranchCodes = [],
  sendNames = false,
  disabled = false,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
   useEffect(() => {
     setExpanded(initiallyExpanded);
   }, [initiallyExpanded]);
   useEffect(() => {
    const updated = {};
    selectedBranchCodes?.forEach((key) => {
      updated[key] = true;
    });
    setCheckedItems(updated);
  }, [selectedBranchCodes]);

  const toggleExpand = () => {
    if (!disabled) {
      setExpanded((prev) => !prev);
    }
  };

  const getKey = (branch) => (sendNames ? branch.name : branch.code);

  const handleCheck = (branch, isChecked) => {
    if (disabled) return;

    const updates = { ...checkedItems };

    const updateBranch = (node) => {
      if (!node?.children || node?.children?.length === 0) {
        const key = getKey(node);
        if (isChecked) {
          updates[key] = true;
        } else {
          delete updates[key];
        }
      } else {
        node?.children?.forEach(updateBranch);
      }
    };

    if (branch?.children) {
      branch?.children?.forEach(updateBranch);
    } else {
      updateBranch(branch);
    }

    setCheckedItems(updates);
    onSelect && onSelect(Object.keys(updates));
  };

  const matchSearch = (name) =>
    name?.toLowerCase()?.includes(searchTerm?.toLowerCase());

  const filterBranches = (branch) => {
    if (matchSearch(branch?.name)) return true;
    if (branch?.children) return branch?.children?.some(filterBranches);
    return false;
  };

  const areAllChildrenChecked = (branch) => {
    if (!branch?.children) return false;
    return branch?.children.every(
      (child) =>
        (!child?.children && checkedItems[getKey(child)]) ||
        (child?.children && areAllChildrenChecked(child))
    );
  };

  const renderBranches = (branch) => {
    if (!filterBranches(branch)) return null;

    const isParent = Boolean(branch?.children);
    const key = getKey(branch);

    return (
      <div
        key={key}
        className={
          isParent ? classes.branchWrapper : classes.branchLeafWrapper
        }
      >
        <div
          className={`${classes.branchRow} ${
            expanded ? classes.parentContainer : ""
          }`}
        >
          {isParent && (
            <button
              onClick={toggleExpand}
              className={classes.childContainer}
              disabled={disabled}
            >
              {expanded ? "-" : "+"}
            </button>
          )}
          <input
            type="checkbox"
            checked={
              isParent
                ? areAllChildrenChecked(branch)
                : checkedItems[key] || false
            }
            onChange={(e) => handleCheck(branch, e.target.checked)}
            disabled={disabled}
          />
          <span style={{ marginLeft: 8 }}>{branch.name}</span>
        </div>

        {isParent && expanded && (
          <div className={classes.childrenContainer}>
            {branch?.children?.map((child) => renderBranches(child))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <CustomeTextField
        label={label}
        id="search"
        searchTop
        disabled={disabled}
        onchange={(e) => {
          setSearchTerm(e.target.value);
          setExpanded(true);
        }}
      />
      <div className={classes.container}>
        {data?.map((branch) => renderBranches(branch))}
      </div>
    </div>
  );
};

CustomTextSelect.propTypes = {
  data: PropTypes.array.isRequired,
  label: PropTypes.string,
  onSelect: PropTypes.func,
  initiallyExpanded: PropTypes.bool,
  selectedBranchCodes: PropTypes.array,
  sendNames: PropTypes.bool,
  disabled: PropTypes.bool,
};

CustomTextSelect.defaultProps = {
  label: "Search branches...",
  initiallyExpanded: false,
  sendNames: false,
  disabled: false,
};

export default CustomTextSelect;
